import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { apiErrorMessage } from './apiErrorMessage';
import type { SecurityRole, User } from '../types/user';

// ===== QUERY KEYS =====
export const roleKeys = {
  all: ['roles'] as const,
  list: (facilityId: number) => [...roleKeys.all, 'list', facilityId] as const,
  detail: (facilityId: number, roleId: number) =>
    [...roleKeys.all, 'detail', facilityId, roleId] as const,
  usage: (facilityId: number, roleId: number) =>
    [...roleKeys.all, 'usage', facilityId, roleId] as const,
};

// ===== QUERIES =====

export function useRoles(facilityId: number) {
  return useQuery({
    queryKey: roleKeys.list(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<SecurityRole[]>(API.roles(facilityId));
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRole(facilityId: number, roleId: number) {
  return useQuery({
    queryKey: roleKeys.detail(facilityId, roleId),
    queryFn: async () => {
      const { data } = await apiClient.get<SecurityRole>(API.roleById(facilityId, roleId));
      return data;
    },
    enabled: facilityId > 0 && roleId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useRoleUsage(facilityId: number, roleId: number) {
  return useQuery({
    queryKey: roleKeys.usage(facilityId, roleId),
    queryFn: async () => {
      const { data } = await apiClient.get<User[]>(API.roleUsage(facilityId, roleId));
      return data;
    },
    enabled: facilityId > 0 && roleId > 0,
    staleTime: 30 * 1000,
  });
}

// ===== MUTATIONS =====

export function useSaveRole(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (role: Partial<SecurityRole>) => {
      const { data } = await apiClient.post<number>(API.roleAddMod(facilityId), role);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      useNotificationStore.getState().addNotification('Role saved', 'success');
    },
    // The API says why it refused (e.g. "You can't give a role more access than your own role
    // has (...)"); a bare "Failed to save role" left the user with nothing to act on.
    onError: (error: unknown) => {
      useNotificationStore.getState().addNotification(apiErrorMessage(error, 'Failed to save role'), 'error');
    },
  });
}

export function useCopyRole(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (roleId: number) => {
      const { data } = await apiClient.post<number>(API.roleCopy(facilityId, roleId));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.list(facilityId) });
      useNotificationStore.getState().addNotification('Role copied', 'success');
    },
    onError: (error: unknown) => {
      useNotificationStore.getState().addNotification(apiErrorMessage(error, 'Failed to copy role'), 'error');
    },
  });
}

export function useReplaceRole(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ oldRoleId, newRoleId }: { oldRoleId: number; newRoleId: number }) => {
      const { data } = await apiClient.post<number>(API.roleReplace(facilityId, oldRoleId, newRoleId));
      return data;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      useNotificationStore.getState().addNotification(`Role replaced on ${count} user(s)`, 'success');
    },
    onError: (error: unknown) => {
      useNotificationStore.getState().addNotification(apiErrorMessage(error, 'Failed to replace role'), 'error');
    },
  });
}

export function useDeleteRole(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (roleId: number) => {
      const { data } = await apiClient.delete<boolean>(API.roleDelete(facilityId, roleId));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.list(facilityId) });
      useNotificationStore.getState().addNotification('Role deleted', 'success');
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const fallback = status === 400 ? 'Role is in use and cannot be deleted' : 'Failed to delete role';
      useNotificationStore.getState().addNotification(apiErrorMessage(error, fallback), 'error');
    },
  });
}
